import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { Upload, Button, Modal, message, Tooltip } from 'antd';
import {
	UploadOutlined,
	EyeOutlined,
	CloseCircleOutlined,
	DeleteOutlined,
	LeftOutlined,
	RightOutlined,
} from '@ant-design/icons';
import { getSignedUrl } from 'store/commonSlice';
import { Box } from 'atoms';

const FileRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	padding: 10px 12px;
	border-radius: 8px;
	cursor: pointer;
	background: ${(props) => (props.$active ? '#f0f6ff' : 'transparent')};
`;

const FileActionsContainer = styled.div`
	// New wrapper to horizontally list uploaded items
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin-top: 10px;
`;

const UploadListItem = styled.div`
	// New wrapper for each uploaded item chip
	display: inline-flex;
	align-items: center;
	gap: 4px;
	background: #f0f6ff;
	padding: 4px 8px;
	border-radius: 4px;
	min-width: 0;
	color: #1890ff;
	font-size: 13px;
`;

const FileName = styled.div`
	flex: 1;
	min-width: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	color: #1f1f1f;
	font-size: 13px;
`;

const IconBtn = styled(Button)`
	border: none !important;
	background: none !important;
	box-shadow: none !important;
	padding: 0 6px !important;
	min-width: 0 !important;
	height: 28px !important;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const PreviewLayout = styled.div`
	display: grid;
	grid-template-columns: 260px 1fr;
	min-height: 520px;
`;

const Sidebar = styled.div`
	border-right: 1px solid #f0f0f0;
	padding: 12px;
	overflow: auto;
`;

const PreviewPane = styled.div`
	padding: 16px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	background: #fff;
`;

const SidebarTitle = styled.div`
	font-weight: 600;
	font-size: 12px;
	letter-spacing: 0.4px;
	color: #6b7280;
	margin-bottom: 10px;
`;

const DotContainer = styled.div`
	display: flex;
	justify-content: center;
	gap: 8px;
	margin-top: 16px;
`;

const Dot = styled.div`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	cursor: pointer;
	transition: 0.3s;
`;

const ArrowButton = styled(Button)`
	border: none;
	background: transparent;
	box-shadow: none;
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	padding: 0 16px;

	.anticon {
		font-size: 24px;
		color: #8c8c8c;
	}

	&:disabled .anticon {
		color: #d9d9d9;
	}

	&:hover {
		background-color: transparent !important;
	}
	&focus {
		background-color: transparent !important;
	}

	&:active {
		background-color: transparent !important;
	}
`;

function getExt(name = '') {
	return String(name).split('.').pop()?.toLowerCase?.() || '';
}

function UploadWithPreviewMultiple({
	value,
	onChange,
	disabled,
	buttonText = 'Upload',
	accept = ['.pdf', '.jpg', '.jpeg', '.png'],
	entity = 'trip',
	maxSizeMb = 10,
	maxCount,
	multiple = true,
	uploadError,
	readonly = false,
}) {
	const dispatch = useDispatch();
	const [uploadingCount, setUploadingCount] = useState(0);
	const [previewOpen, setPreviewOpen] = useState(false);
	const [activeKey, setActiveKey] = useState('');
	const [activeUrl, setActiveUrl] = useState('');
	const [loadingPreview, setLoadingPreview] = useState(false);

	const files = useMemo(() => (Array.isArray(value) ? value.filter(Boolean) : []), [value]);
	const latestFilesRef = useRef(files);

	useEffect(() => {
		latestFilesRef.current = files;
	}, [files]);

	const canUploadMore =
		typeof maxCount === 'number' ? files.length + uploadingCount < maxCount : true;
	const uploading = uploadingCount > 0;

	const emitChange = (next) => {
		if (onChange) onChange(next);
	};

	const handlePrev = () => {
		const idx = files.indexOf(activeKey);
		if (idx > 0) openPreviewFor(files[idx - 1]);
	};

	const handleNext = () => {
		const idx = files.indexOf(activeKey);
		if (idx < files.length - 1) openPreviewFor(files[idx + 1]);
	};

	const validateFile = (file) => {
		const ext = `.${getExt(file.name)}`;
		if (accept?.length && !accept.includes(ext)) {
			message.error(`Only ${accept.join(', ')} files are allowed`);
			return false;
		}
		const isLt = file.size / 1024 / 1024 < maxSizeMb;
		if (!isLt) {
			message.error(`File must be smaller than ${maxSizeMb}MB`);
			return false;
		}
		return true;
	};

	const uploadSingleFile = async (file) => {
		const signedUrlResponse = await dispatch(
			getSignedUrl({
				url_type: 'upload',
				data: {
					entity,
					keys: [file.name],
					content_type: file.type || 'application/octet-stream',
				},
			})
		);

		const fileData = signedUrlResponse?.data?.[0];
		if (!fileData?.url || !fileData?.filename) {
			throw new Error('Failed to get upload URL');
		}

		const putRes = await fetch(fileData.url, {
			method: 'PUT',
			body: file,
			headers: {
				'Content-Type': file.type || 'application/octet-stream',
			},
		});

		if (!putRes.ok) {
			throw new Error('Failed to upload file');
		}

		return fileData.filename;
	};

	const startUpload = async (file) => {
		setUploadingCount((c) => c + 1);
		try {
			const uploadedKey = await uploadSingleFile(file);
			const next = [...(latestFilesRef.current || []), uploadedKey];
			emitChange(next);
			message.success('File uploaded');
		} catch (e) {
			console.error('Upload error:', e);
			message.error(e?.message || 'Failed to upload file');
		} finally {
			setUploadingCount((c) => Math.max(0, c - 1));
		}
	};

	const handleBeforeUpload = (file) => {
		if (disabled) return Upload.LIST_IGNORE;
		if (
			typeof maxCount === 'number' &&
			(latestFilesRef.current?.length || 0) + uploadingCount >= maxCount
		) {
			message.info(`You can upload up to ${maxCount} file(s)`);
			return Upload.LIST_IGNORE;
		}
		if (!validateFile(file)) return Upload.LIST_IGNORE;

		startUpload(file);
		return Upload.LIST_IGNORE;
	};

	const openPreviewFor = async (fileKey) => {
		if (!fileKey) return;
		setActiveKey(fileKey);
		setPreviewOpen(true);
		setLoadingPreview(true);
		setActiveUrl('');

		try {
			const res = await dispatch(
				getSignedUrl({
					url_type: 'view',
					data: {
						entity,
						keys: [fileKey],
					},
				})
			);
			const url = res?.data?.[0]?.url;
			if (!url) {
				message.info('Preview not available');
				return;
			}
			setActiveUrl(url);
		} catch (err) {
			message.info('Preview not available');
		} finally {
			setLoadingPreview(false);
		}
	};

	const removeFile = (fileKey) => {
		const next = (latestFilesRef.current || []).filter((k) => k !== fileKey);
		emitChange(next);

		if (activeKey === fileKey) {
			const fallback = next[0] || '';
			if (fallback) openPreviewFor(fallback);
			else {
				setPreviewOpen(false);
				setActiveKey('');
				setActiveUrl('');
			}
		}
	};

	const renderPreview = () => {
		if (loadingPreview) {
			return <div style={{ color: '#6b7280' }}>Loading preview…</div>;
		}
		if (!activeUrl || !activeKey) {
			return <div style={{ color: '#6b7280' }}>Select a file to preview</div>;
		}

		const ext = getExt(activeKey);
		if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
			return (
				<img
					alt={activeKey}
					src={activeUrl}
					style={{ maxWidth: '100%', maxHeight: '520px', objectFit: 'contain' }}
				/>
			);
		}

		if (ext === 'pdf') {
			return (
				<Box width="100%">
					<div
						style={{
							marginBottom: 12,
							display: 'flex',
							gap: 10,
							justifyContent: 'center',
						}}
					>
						<Button type="primary" onClick={() => window.open(activeUrl, '_blank')}>
							Open PDF in New Tab
						</Button>
					</div>
					<iframe
						title={activeKey}
						src={`${activeUrl}#toolbar=0&navpanes=0&scrollbar=0`}
						width="100%"
						height="520px"
						style={{ border: '1px solid #f0f0f0', borderRadius: 8 }}
					/>
				</Box>
			);
		}

		return (
			<div style={{ textAlign: 'center' }}>
				<p style={{ marginBottom: 12 }}>Preview not available for this file type.</p>
				<Button type="primary" onClick={() => window.open(activeUrl, '_blank')}>
					Open in New Tab
				</Button>
			</div>
		);
	};

	return (
		<div>
			{!readonly && (
				<Upload
					disabled={disabled || uploading || !canUploadMore}
					multiple={multiple}
					showUploadList={false}
					accept={accept.join(',')}
					beforeUpload={handleBeforeUpload}
				>
					<Button
						icon={<UploadOutlined />}
						loading={uploading}
						disabled={disabled || uploading || !canUploadMore}
					>
						{buttonText}
					</Button>
				</Upload>
			)}

			{uploadError && <div style={{ color: 'red', marginTop: 4 }}>{uploadError}</div>}

			{files.length > 0 && (
				<FileActionsContainer>
					{files.map((fileKey) => (
						<UploadListItem key={fileKey}>
							<span
								style={{
									maxWidth: 160,
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
								}}
								title={fileKey}
							>
								Uploaded: {fileKey}
							</span>
							<Tooltip title="Preview">
								<IconBtn
									type="text"
									icon={<EyeOutlined style={{ color: '#1890ff' }} />}
									onClick={() => openPreviewFor(fileKey)}
									disabled={disabled}
								/>
							</Tooltip>
							{!readonly && (
								<Tooltip title="Remove">
									<IconBtn
										type="text"
										icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
										onClick={() => removeFile(fileKey)}
										disabled={disabled}
									/>
								</Tooltip>
							)}
						</UploadListItem>
					))}
				</FileActionsContainer>
			)}

			<Modal
				open={previewOpen}
				title={null}
				footer={null}
				onCancel={() => setPreviewOpen(false)}
				centered
				width={1100}
				destroyOnClose
			>
				<PreviewLayout>
					<Sidebar>
						<SidebarTitle>PREVIEW DOCUMENT</SidebarTitle>
						{files.map((fileKey) => (
							<FileRow
								key={fileKey}
								$active={fileKey === activeKey}
								onClick={() => openPreviewFor(fileKey)}
							>
								<FileName title={fileKey}>{fileKey}</FileName>
								{fileKey === activeKey && (
									<div style={{ marginRight: 'auto', paddingLeft: 8 }}>
										<svg
											width="14"
											height="14"
											viewBox="0 0 14 14"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M5.25 10.5L8.75 7L5.25 3.5"
												stroke="#1f1f1f"
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</div>
								)}
								{!readonly && (
									<Tooltip title="Remove">
										<IconBtn
											type="text"
											icon={
												<CloseCircleOutlined style={{ color: '#ff4d4f' }} />
											}
											onClick={(e) => {
												e.stopPropagation();
												removeFile(fileKey);
											}}
										/>
									</Tooltip>
								)}
							</FileRow>
						))}
					</Sidebar>

					<PreviewPane>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								width: '100%',
								flex: 1,
								minHeight: 0,
							}}
						>
							<ArrowButton
								icon={<LeftOutlined />}
								onClick={handlePrev}
								disabled={!files.length || files.indexOf(activeKey) <= 0}
								style={{ visibility: files.length > 1 ? 'visible' : 'hidden' }}
							/>
							<div
								style={{
									flex: 1,
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									overflow: 'hidden',
								}}
							>
								{renderPreview()}
							</div>
							<ArrowButton
								icon={<RightOutlined />}
								onClick={handleNext}
								disabled={
									!files.length || files.indexOf(activeKey) === files.length - 1
								}
								style={{ visibility: files.length > 1 ? 'visible' : 'hidden' }}
							/>
						</div>
						{files.length > 1 && (
							<DotContainer>
								{files.map((fileKey) => (
									<Dot
										key={fileKey}
										$active={fileKey === activeKey}
										onClick={() => openPreviewFor(fileKey)}
									/>
								))}
							</DotContainer>
						)}
						<div
							style={{
								marginTop: 24,
								display: 'flex',
								gap: 12,
								justifyContent: 'center',
								width: '100%',
								borderTop: '1px solid #f0f0f0',
								paddingTop: 16,
							}}
						>
							<Button onClick={() => setPreviewOpen(false)} style={{ minWidth: 120 }}>
								Close
							</Button>
							{!readonly && (
								<Button
									danger
									type="primary"
									icon={<DeleteOutlined />}
									style={{ minWidth: 120 }}
									disabled={!activeKey}
									onClick={() => {
										if (!activeKey) return;
										removeFile(activeKey);
									}}
								>
									Delete
								</Button>
							)}
						</div>
					</PreviewPane>
				</PreviewLayout>
			</Modal>
		</div>
	);
}

UploadWithPreviewMultiple.propTypes = {
	value: PropTypes.arrayOf(PropTypes.string),
	onChange: PropTypes.func,
	disabled: PropTypes.bool,
	buttonText: PropTypes.string,
	accept: PropTypes.arrayOf(PropTypes.string),
	entity: PropTypes.string,
	maxSizeMb: PropTypes.number,
	maxCount: PropTypes.number,
	multiple: PropTypes.bool,
	uploadError: PropTypes.string,
	readonly: PropTypes.bool,
};

export default UploadWithPreviewMultiple;
